import { Injectable } from '@angular/core';

export type SoundType = 'success' | 'error' | 'notification' | 'alert';

/**
 * Service for playing audio notifications.
 * Uses the Audio API to play sounds from assets/sounds/
 */
@Injectable({
    providedIn: 'root'
})
export class AudioService {
    private sounds = new Map<SoundType, HTMLAudioElement>();
    private isEnabled = true;
    private audioContext: AudioContext | null = null;

    constructor() {
        this.preloadSounds();
    }

    /**
     * Preload sounds for faster playback
     */
    private preloadSounds(): void {
        const soundFiles: Record<SoundType, string> = {
            success: 'assets/sounds/success.mp3',
            error: 'assets/sounds/error.mp3',
            notification: 'assets/sounds/success.mp3', // Use success for notifications
            alert: 'assets/sounds/alert.mp3' // Fallback: coloca alert.mp3 o usa error.mp3
        };

        for (const [type, path] of Object.entries(soundFiles)) {
            const audio = new Audio(path);
            audio.preload = 'auto';
            audio.volume = 0.5;
            this.sounds.set(type as SoundType, audio);
        }
    }

    /**
     * Play a sound by type
     */
    play(type: SoundType): void {
        if (!this.isEnabled) return;

        const audio = this.sounds.get(type);
        if (audio) {
            // Clone the audio to allow overlapping sounds
            const clone = audio.cloneNode() as HTMLAudioElement;
            clone.volume = audio.volume;
            clone.play().catch(err => {
                // Browser may block autoplay - this is expected before user interaction
                console.debug('[AudioService] Could not play sound:', err.message);
            });
        }
    }

    /**
     * Play sound for device connected
     */
    playDeviceConnected(): void {
        this.play('success');
    }

    /**
     * Play sound for device disconnected
     */
    playDeviceDisconnected(): void {
        this.play('error');
    }

    /**
     * Play sound for new notification
     */
    playNotification(): void {
        this.play('notification');
    }

    /**
     * Play alert sound for denied vehicle detection
     */
    playDeniedAlert(): void {
        this.play('alert');
    }

    /**
     * El navegador bloquea el audio hasta que hay un clic. Llamar desde un gesto.
     */
    unlock(): void {
        const ctx = this.context();
        if (ctx.state === 'suspended') {
            void ctx.resume();
        }
    }

    /**
     * Aviso corto de una detección con patente.
     * Los mp3 de assets/sounds están vacíos, así que el tono se genera acá.
     */
    playDetectionChime(): void {
        this.playTone(880, 0.18);
    }

    /**
     * Enable or disable sounds
     */
    setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
    }

    /**
     * Check if sounds are enabled
     */
    getEnabled(): boolean {
        return this.isEnabled;
    }

    private context(): AudioContext {
        if (!this.audioContext) {
            this.audioContext = new AudioContext();
        }
        return this.audioContext;
    }

    private playTone(frequency: number, durationSec: number): void {
        if (!this.isEnabled) return;
        const ctx = this.context();
        if (ctx.state === 'suspended') {
            void ctx.resume();
        }
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(frequency, now);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + durationSec);
    }
}
